import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Star, MapPin, Clock, ChevronLeft, ChevronRight, ArrowUpDown, Phone, MessageCircle, Award, X, ChevronDown, User } from 'lucide-react';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCurrency } from '../utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getPhotoUrl = (path) => {
  if (!path) return null;
  return `${BACKEND_URL}/api/lawyers/photos/${path}`;
};

// All Indian States and Union Territories
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Major Indian Cities by State
const CITIES_BY_STATE = {
  'Delhi': ['New Delhi', 'Central Delhi', 'South Delhi', 'North Delhi', 'East Delhi', 'West Delhi', 'Dwarka', 'Rohini'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Navi Mumbai'],
  'Karnataka': ['Bangalore', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum', 'Gulbarga', 'Davangere'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Vellore'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Secunderabad'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Darjeeling'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Alwar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Noida', 'Ghaziabad', 'Prayagraj', 'Meerut'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam'],
  'Punjab': ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Rohtak', 'Hisar'],
  'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'],
  'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Brahmapur', 'Sambalpur'],
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Tirupati', 'Rajahmundry'],
  'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh'],
  'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg'],
  'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon'],
  'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rishikesh'],
  'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Solan', 'Mandi', 'Kullu'],
  'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda'],
  'Jammu & Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla'],
  'Puducherry': ['Puducherry', 'Karaikal'],
  'Chandigarh': ['Chandigarh'],
};

// Popular Cities for Quick Selection
const POPULAR_CITIES = [
  'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad',
  'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Gurugram'
];

// All Legal Categories
const LEGAL_CATEGORIES = [
  { name: 'Criminal', icon: '⚖️', desc: 'Bail, FIR, Defense, Appeals' },
  { name: 'Civil', icon: '📋', desc: 'Disputes, Recovery, Injunctions' },
  { name: 'Family', icon: '👨‍👩‍👧', desc: 'Divorce, Custody, Maintenance, Adoption' },
  { name: 'Property', icon: '🏠', desc: 'Land, Real Estate, Title, Registration' },
  { name: 'Corporate', icon: '🏢', desc: 'Business, Contracts, Compliance' },
  { name: 'Tax', icon: '📊', desc: 'GST, Income Tax, TDS, Appeals' },
  { name: 'Labour', icon: '👷', desc: 'Employment, PF, ESI, Disputes' },
  { name: 'Consumer', icon: '🛒', desc: 'Product Issues, Services, Refunds' },
  { name: 'Banking', icon: '🏦', desc: 'Loans, NPA, SARFAESI, Recovery' },
  { name: 'Insurance', icon: '🛡️', desc: 'Claims, Disputes, IRDAI' },
  { name: 'Cyber Law', icon: '💻', desc: 'Online Fraud, Data Privacy, IT Act' },
  { name: 'Intellectual Property', icon: '💡', desc: 'Patents, Trademarks, Copyrights' },
  { name: 'Immigration', icon: '✈️', desc: 'Visa, Citizenship, Passport' },
  { name: 'Motor Accident', icon: '🚗', desc: 'Claims, Insurance, Compensation' },
  { name: 'Cheque Bounce', icon: '📝', desc: 'NI Act, Section 138, Recovery' },
  { name: 'Documentation', icon: '📄', desc: 'Agreements, Wills, POA, Affidavits' },
  { name: 'Arbitration', icon: '🤝', desc: 'Mediation, Dispute Resolution' },
  { name: 'Constitutional', icon: '📜', desc: 'Fundamental Rights, PIL, Writs' },
];

// Budget Options
const BUDGET_OPTIONS = [
  { label: 'Under ₹500', max: 500 },
  { label: 'Under ₹1,000', max: 1000 },
  { label: 'Under ₹2,000', max: 2000 },
  { label: 'Under ₹3,000', max: 3000 },
  { label: 'Under ₹5,000', max: 5000 },
  { label: 'Under ₹7,500', max: 7500 },
  { label: 'Under ₹10,000', max: 10000 },
  { label: 'Any Budget', max: 50000 },
];

// Sort Options
const SORT_OPTIONS = [
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'experience', label: 'Most Experienced' },
  { value: 'reviews', label: 'Most Reviews' },
];

const LawyerSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('price_low');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    maxFee: 50000,
    page: 1
  });

  const sortLawyers = useCallback((lawyersList, sortOption) => {
    const sorted = [...lawyersList];
    switch (sortOption) {
      case 'price_low':
        return sorted.sort((a, b) => a.consultation_fee - b.consultation_fee);
      case 'price_high':
        return sorted.sort((a, b) => b.consultation_fee - a.consultation_fee);
      case 'rating':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'experience':
        return sorted.sort((a, b) => b.years_of_experience - a.years_of_experience);
      case 'reviews':
        return sorted.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
      default:
        return sorted;
    }
  }, []);

  const fetchLawyers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.location) params.set('location', filters.location);
      if (filters.maxFee < 50000) params.set('max_fee', filters.maxFee);
      params.set('page', filters.page);
      params.set('limit', 12);

      const res = await api.get(`/lawyers/search?${params.toString()}`);
      let fetchedLawyers = res.data.lawyers || [];
      fetchedLawyers = sortLawyers(fetchedLawyers, sortBy);
      setLawyers(fetchedLawyers);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching lawyers:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortLawyers]);

  useEffect(() => {
    fetchLawyers();
  }, [fetchLawyers]);

  useEffect(() => {
    if (lawyers.length > 0) {
      setLawyers(prev => sortLawyers([...prev], sortBy));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortLawyers]);

  const clearFilters = () => {
    setFilters({ category: '', location: '', maxFee: 50000, page: 1 });
    setSelectedState('');
    setSearchParams({});
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setFilters({ ...filters, location: '', page: 1 });
  };

  const handleCitySelect = (city) => {
    setFilters({ ...filters, location: city, page: 1 });
  };

  const getCitiesForState = () => {
    if (!selectedState) return [];
    return CITIES_BY_STATE[selectedState] || [];
  };

  const displayedCategories = showAllCategories ? LEGAL_CATEGORIES : LEGAL_CATEGORIES.slice(0, 9);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Find a Lawyer Near You</h1>
          <p className="text-slate-500 mt-1">Best lawyers at affordable prices across India</p>
        </div>

        {/* Location Selection - State & City */}
        <div className="bg-white rounded-xl p-4 mb-4 border border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-amber-600" />
            <p className="font-medium text-slate-900">Select Location</p>
          </div>
          
          {/* Popular Cities */}
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2">Popular Cities</p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_CITIES.map(city => (
                <button
                  key={city}
                  onClick={() => handleCitySelect(city)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    filters.location === city
                      ? 'bg-amber-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-amber-100'
                  }`}
                  data-testid={`city-${city.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>

          {/* State & City Dropdowns */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-500 mb-1">Select State</p>
              <Select value={selectedState} onValueChange={handleStateChange}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Choose State" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Select City</p>
              {selectedState ? (
                <Select 
                  value={filters.location} 
                  onValueChange={(city) => setFilters({ ...filters, location: city, page: 1 })}
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="Choose City" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {getCitiesForState().map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="text"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value, page: 1 })}
                  placeholder="Type city name..."
                  data-testid="location-input"
                />
              )}
            </div>
          </div>
        </div>

        {/* Legal Categories */}
        <div className="bg-white rounded-xl p-4 mb-4 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium text-slate-900">Legal Category</p>
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="text-sm text-amber-600 hover:underline flex items-center gap-1"
            >
              {showAllCategories ? 'Show Less' : `View All (${LEGAL_CATEGORIES.length})`}
              <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
            {displayedCategories.map(cat => (
              <button
                key={cat.name}
                onClick={() => setFilters({ ...filters, category: filters.category === cat.name ? '' : cat.name, page: 1 })}
                className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                  filters.category === cat.name
                    ? 'bg-amber-50 border-2 border-amber-500 shadow-sm'
                    : 'bg-slate-50 border border-slate-200 hover:border-amber-300'
                }`}
                data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="text-xl">{cat.icon}</span>
                <span className="text-xs mt-1 text-center text-slate-700 line-clamp-1">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Budget & Sort */}
        <div className="bg-white rounded-xl p-4 mb-4 border border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Budget */}
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700 mb-2">Your Budget (per consultation)</p>
              <div className="flex flex-wrap gap-2">
                {BUDGET_OPTIONS.map((budget) => (
                  <button
                    key={budget.max}
                    onClick={() => setFilters({ ...filters, maxFee: budget.max, page: 1 })}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      filters.maxFee === budget.max
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`budget-${budget.max}`}
                  >
                    {budget.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="lg:w-52">
              <p className="text-sm font-medium text-slate-700 mb-2">Sort By</p>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {(filters.category || filters.location || filters.maxFee < 50000) && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm text-slate-500">Filters:</span>
            {filters.location && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {filters.location}
                <button onClick={() => setFilters({ ...filters, location: '', page: 1 })}><X className="w-3 h-3" /></button>
              </span>
            )}
            {filters.category && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm flex items-center gap-1">
                {filters.category}
                <button onClick={() => setFilters({ ...filters, category: '', page: 1 })}><X className="w-3 h-3" /></button>
              </span>
            )}
            {filters.maxFee < 50000 && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm flex items-center gap-1">
                Budget: {formatCurrency(filters.maxFee)}
                <button onClick={() => setFilters({ ...filters, maxFee: 50000, page: 1 })}><X className="w-3 h-3" /></button>
              </span>
            )}
            <button onClick={clearFilters} className="text-sm text-red-600 hover:underline">Clear All</button>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-slate-600">
            {loading ? 'Searching...' : `Found ${lawyers.length} lawyer${lawyers.length !== 1 ? 's' : ''}`}
            {filters.location && ` in ${filters.location}`}
            {filters.category && ` for ${filters.category}`}
          </p>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : lawyers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No lawyers found</h3>
            <p className="text-slate-500 mt-2">Try changing location, category or budget</p>
            <Button onClick={clearFilters} className="mt-4">Clear Filters</Button>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lawyers.map((lawyer, idx) => (
                <motion.div
                  key={lawyer.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg hover:border-amber-300 transition-all"
                  data-testid={`lawyer-card-${idx}`}
                >
                  {/* Rating Badge */}
                  {lawyer.rating >= 4 && (
                    <div className="mb-2">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                        ⭐ Top Rated
                      </span>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center text-xl font-bold text-amber-700 shrink-0 overflow-hidden">
                      {lawyer.profile_photo ? (
                        <img
                          src={getPhotoUrl(lawyer.profile_photo)}
                          alt={lawyer.user?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        lawyer.user?.name?.charAt(0) || 'L'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{lawyer.user?.name}</h3>
                      <p className="text-sm text-slate-500">{lawyer.qualification}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-medium">{lawyer.rating || 0}</span>
                          <span className="text-xs text-slate-400">({lawyer.review_count || 0})</span>
                        </div>
                        <span className="text-slate-300">•</span>
                        <span className="text-sm text-slate-500">{lawyer.years_of_experience} yrs</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span className="truncate">{lawyer.user?.location || 'India'}</span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {lawyer.specializations?.slice(0, 3).map(spec => (
                      <span key={spec} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                        {spec}
                      </span>
                    ))}
                  </div>

                  {/* Languages */}
                  {lawyer.languages && lawyer.languages.length > 0 && (
                    <div className="mt-2 text-xs text-slate-500">
                      🗣️ {lawyer.languages.slice(0, 3).join(', ')}
                    </div>
                  )}

                  {/* Mobile Number - Visible only to logged-in clients */}
                  {user && user.role === 'client' && lawyer.user?.mobile && (
                    <div className="mt-3 flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                      <Phone className="w-4 h-4 text-emerald-600" />
                      <a 
                        href={`tel:${lawyer.user.mobile}`}
                        className="text-sm font-medium text-emerald-700 hover:underline"
                        data-testid={`lawyer-mobile-${idx}`}
                      >
                        {lawyer.user.mobile}
                      </a>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500">Consultation Fee</p>
                        <p className="text-xl font-bold text-slate-900">{formatCurrency(lawyer.consultation_fee)}</p>
                      </div>
                      <Link
                        to={`/lawyers/${lawyer.id}`}
                        className="px-4 py-2 bg-amber-600 text-white rounded-full text-sm font-medium hover:bg-amber-700 transition-colors"
                        data-testid={`book-lawyer-${idx}`}
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
                  disabled={filters.page === 1}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="px-4 py-2 text-sm text-slate-600">
                  Page {filters.page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setFilters({ ...filters, page: Math.min(totalPages, filters.page + 1) })}
                  disabled={filters.page === totalPages}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LawyerSearchPage;
